import React from 'react'
import { Box, styled } from '@mui/material'

/**
 * The 4 states a person's connection can be in relative to the current user —
 * shared by ConnectionsScreen (Requests/Search tabs) and the global search
 * results People tab, so the button styling/logic only lives in one place.
 */
export type PersonActionState = 'connected' | 'sent' | 'received' | 'none'

export interface PersonActionButtonsProps {
  state: PersonActionState
  /** Disables the Decline/Accept pair while a request is in flight for this row. */
  busy?: boolean
  /** Disables the Cancel affordance while a cancel is in flight for this row. */
  cancelling?: boolean
  onConnect?: () => void
  onAccept?: () => void
  onDecline?: () => void
  /** Withdraws a request *this* user sent — only relevant when state is 'sent'. */
  onCancelSent?: () => void
}

const Pill = styled(Box)({
  height: 32,
  display: 'flex',
  alignItems: 'center',
  padding: '0 15px',
  borderRadius: 'var(--ds-radius-pill)',
  fontSize: 12.5,
  fontWeight: 640,
  background: 'var(--ds-hover)',
  color: 'var(--ds-ink-subtle)',
  whiteSpace: 'nowrap',
})

const OutlineBtn = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  height: 32,
  padding: '0 15px',
  borderRadius: 'var(--ds-radius-pill)',
  fontSize: 12.5,
  fontWeight: 640,
  color: 'var(--ds-ink)',
  boxShadow: 'inset 0 0 0 1px var(--ds-border-strong)',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  transition: 'box-shadow var(--ds-duration-fast) var(--ds-ease), opacity var(--ds-duration-fast) var(--ds-ease)',
  '&:hover': { boxShadow: 'inset 0 0 0 1px var(--ds-ink)' },
  '&[disabled]': { opacity: 0.5, cursor: 'default', pointerEvents: 'none' },
})

const SolidBtn = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  height: 32,
  padding: '0 15px',
  borderRadius: 'var(--ds-radius-pill)',
  fontSize: 12.5,
  fontWeight: 640,
  background: 'var(--ds-ink)',
  color: 'var(--ds-inverse-ink)',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  transition: 'opacity var(--ds-duration-fast) var(--ds-ease)',
  '&:hover': { opacity: 0.85 },
})

/** Renders the Connect / Requested / Decline+Accept / Connected states used anywhere a person row needs a connection action. */
export function PersonActionButtons({ state, busy, cancelling, onConnect, onAccept, onDecline, onCancelSent }: PersonActionButtonsProps) {
  if (state === 'connected') return <Pill>Connected</Pill>
  if (state === 'sent') {
    if (!onCancelSent) return <Pill>Requested</Pill>
    return (
      <OutlineBtn type='button' disabled={cancelling} onClick={onCancelSent}>
        {cancelling ? 'Cancelling…' : 'Cancel request'}
      </OutlineBtn>
    )
  }
  if (state === 'received') {
    return (
      <>
        <OutlineBtn type='button' disabled={busy} onClick={onDecline}>
          Decline
        </OutlineBtn>
        <SolidBtn type='button' onClick={onAccept}>
          Accept
        </SolidBtn>
      </>
    )
  }
  return (
    <SolidBtn type='button' onClick={onConnect}>
      Connect
    </SolidBtn>
  )
}
